/**
 * Branch Selector Component
 *
 * Provides a dropdown UI for users to switch between branches when they
 * have access to multiple branches. Automatically hidden for single-branch users.
 *
 * Features:
 * - Auto-hide for single branch
 * - Clear cached data on switch
 * - Refresh Data Hub table if visible
 * - Persist selection to store (which saves to localStorage)
 */

import {
  getUserBranches,
  getSelectedBranchId,
  setSelectedBranchId,
  clearAllViewData,
  store,
} from '@/store'
import { clearSummariesCache } from '@/services/localCacheService'
import { populateCompiledDataTable } from '@/data-hub/table'

/**
 * Handle branch selection change
 */
async function handleBranchChange(event: Event): Promise<void> {
  const select = event.target as HTMLSelectElement
  const newBranchId = select.value
  const oldBranchId = getSelectedBranchId()

  // Skip if same branch selected
  if (newBranchId === oldBranchId) {
    return
  }

  console.log(`[BRANCH_SELECTOR] Switching from ${oldBranchId} to ${newBranchId}`)

  // Disable selector during switch to prevent multiple concurrent switches
  select.disabled = true

  try {
    // 1. Update store (this will trigger localStorage save via account init)
    setSelectedBranchId(newBranchId)
    console.log('[BRANCH_SELECTOR] Store updated with new branch')

    // 2. Clear IndexedDB cache (premium analysis cache)
    try {
      await clearSummariesCache()
      console.log('[BRANCH_SELECTOR] IndexedDB cache cleared')
    } catch (cacheError) {
      console.warn('[BRANCH_SELECTOR] Failed to clear IndexedDB cache:', cacheError)
      // Continue anyway, cache clear is not critical
    }

    // 3. Clear Zustand view data
    clearAllViewData()
    console.log('[BRANCH_SELECTOR] View data cleared from store')

    // 4. Refresh Data Hub table if currently visible
    const dataHubView = document.getElementById('data-hub-view')
    if (dataHubView && !dataHubView.classList.contains('hidden')) {
      console.log('[BRANCH_SELECTOR] Data Hub is visible, refreshing table...')
      try {
        await populateCompiledDataTable()
        console.log('[BRANCH_SELECTOR] Data Hub table refreshed')
      } catch (tableError) {
        console.error('[BRANCH_SELECTOR] Failed to refresh Data Hub table:', tableError)
        // Show error but don't fail the whole operation
        alert('Branch switched successfully, but failed to refresh data table. Please refresh the page.')
      }
    }

    console.log('[BRANCH_SELECTOR] Branch switch completed successfully')

    // Optional: Show success message (subtle, non-intrusive)
    const branchName = getUserBranches().find((b) => b.branchId === newBranchId)?.branchName || newBranchId
    console.log(`[BRANCH_SELECTOR] Now viewing data for: ${branchName}`)
  } catch (error) {
    console.error('[BRANCH_SELECTOR] Error during branch switch:', error)

    // Revert selection in UI (store might be updated, but at least UI shows correct state)
    if (oldBranchId) {
      select.value = oldBranchId
    }

    alert('Failed to switch branch. Please try again or refresh the page.')
  } finally {
    // Re-enable selector
    select.disabled = false
  }
}

/**
 * Create and inject the branch selector component
 *
 * This function:
 * 1. Gets user branches from store
 * 2. Hides if only 1 branch
 * 3. Creates HTML structure
 * 4. Populates dropdown with branches
 * 5. Attaches event handlers
 * 6. Injects into container
 */
export function createBranchSelector(): void {
  console.log('[BRANCH_SELECTOR] Initializing branch selector')

  // Get container element
  const container = document.getElementById('branch-selector-container')
  if (!container) {
    console.warn('[BRANCH_SELECTOR] Container element not found. Branch selector not created.')
    return
  }

  // Get user branches from store
  const branches = getUserBranches()
  const selectedBranchId = getSelectedBranchId()

  // If branches haven't been loaded yet, subscribe to the zustand store once
  // and recreate the selector when branches become available. This prevents
  // the selector from hiding prematurely when account context loads async.
  if (!branches || branches.length === 0) {
    console.log('[BRANCH_SELECTOR] No branches loaded yet, subscribing to store updates')

    try {
      const maybeSubscribe = (store as unknown as { subscribe?: (fn: () => void) => (() => void) | void }).subscribe
      if (typeof maybeSubscribe === 'function') {
        const unsub = maybeSubscribe(() => {
          const updated = getUserBranches()
          if (updated && updated.length > 0) {
            if (typeof unsub === 'function') {
              try {
                unsub()
              } catch (_err) {
                // ignore
              }
            }
            setTimeout(createBranchSelector, 0)
          }
        })

        // If subscribe returned nothing (older runtime), fallback to polling
        if (!unsub) setTimeout(createBranchSelector, 500)
      } else {
        setTimeout(createBranchSelector, 500)
      }
    } catch (err) {
      console.warn('[BRANCH_SELECTOR] Failed to subscribe to store, falling back to timeout polling', err)
      setTimeout(createBranchSelector, 500)
    }

    return
  }

  console.log(`[BRANCH_SELECTOR] User has ${branches.length} branch(es)`)

  // Hide selector if user has only one branch
  if (branches.length <= 1) {
    console.log('[BRANCH_SELECTOR] Hiding selector (user has only 1 branch)')
    container.style.display = 'none'
    return
  }

  // TODO: Temporary disable branch selector
  container.style.display = 'none'
  return;

  // Create branch selector HTML
  const selectorHTML = `
    <div id="branch-selector" class="flex items-center gap-3 bg-white px-4 py-3 rounded-lg shadow-md">
      <label for="branch-selector-dropdown" class="text-sm font-medium text-gray-700 whitespace-nowrap">
        <svg class="w-4 h-4 inline-block mr-1 -mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
        </svg>
        Branch:
      </label>
      <select
        id="branch-selector-dropdown"
        class="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white cursor-pointer min-w-[200px]"
      >
        ${branches.map((branch) => `
          <option value="${branch.branchId}" ${branch.branchId === selectedBranchId ? 'selected' : ''}>
            ${branch.branchName || branch.branchId}
          </option>
        `).join('')}
      </select>
    </div>
  `

  // Inject HTML into container
  container.innerHTML = selectorHTML
  container.style.display = 'block'

  // Attach change event listener
  const dropdown = document.getElementById('branch-selector-dropdown') as HTMLSelectElement
  if (dropdown) {
    dropdown.addEventListener('change', handleBranchChange)
    console.log('[BRANCH_SELECTOR] Branch selector created and event listener attached')
  } else {
    console.error('[BRANCH_SELECTOR] Failed to find dropdown element after injection')
  }
}

/**
 * Refresh the branch selector (useful after account context changes)
 *
 * This can be called if the user's branches change (e.g., after accepting
 * an invitation or if branches are added/removed)
 */
export function refreshBranchSelector(): void {
  console.log('[BRANCH_SELECTOR] Refreshing branch selector')

  // Simply recreate the selector
  createBranchSelector()
}
